import { useContext, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import './Sidebar.css';
import { AuthContext } from '../contexts/AuthContext';
import { 
    FaHome, 
    FaTruck, 
    FaTruckLoading, 
    FaRegFileAlt, 
    FaEnvelope, 
    FaSignOutAlt,
    FaChartBar,
    FaCompass,
    FaTable,
    FaWpforms,
    FaChevronDown,
    FaChevronRight
} from 'react-icons/fa';

const Sidebar = ({ isCollapsed = false, isMobile = false, showMobileOverlay = false, onOverlayClose }) => {
    const { setLoggedIn } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedSections, setExpandedSections] = useState(['analytics', 'navigation', 'tables', 'forms']);

    // Navigation sections data structure
    const navigationSections = [
        {
            id: 'analytics',
            label: 'Analytics',
            icon: FaChartBar,
            items: [
                { path: '/tms/home', label: 'Dashboard', icon: FaHome }
            ]
        },
        {
            id: 'navigation',
            label: 'Navigation',
            icon: FaCompass,
            items: [
                { path: '/tms/quotes', label: 'Quotes', icon: FaTruck },
                { path: '/tms/spot-quotes', label: 'Spot Quotes', icon: FaTruckLoading }
            ]
        },
        {
            id: 'tables',
            label: 'Tables',
            icon: FaTable,
            items: [
                { path: '/tms/booked-shipments', label: 'Booked Shipments', icon: FaRegFileAlt }
            ]
        },
        {
            id: 'forms',
            label: 'Forms',
            icon: FaWpforms,
            items: [
                { path: '/tms/contact', label: 'Contact', icon: FaEnvelope }
            ]
        }
    ];

    const toggleSection = (sectionId) => {
        if (isCollapsed) return; // Don't toggle sections when sidebar is collapsed
        
        setExpandedSections(prev => 
            prev.includes(sectionId) 
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const handleLogout = async () => {
        await fetch('/api/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });
        localStorage.removeItem('token');
        setLoggedIn(false);
        navigate('/tms/login');
    };

    // Handle mobile navigation item click
    const handleMobileNavClick = () => {
        if (isMobile && onOverlayClose) {
            onOverlayClose();
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (event, action, ...args) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            action(...args);
        }
    };

    return (
        <aside 
            id="main-sidebar"
            className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${showMobileOverlay ? 'mobile-visible' : ''}`}
            role="navigation"
            aria-label="Main navigation"
        >
            <Nav 
                className="flex-column"
                role="menubar"
                aria-label="Navigation menu"
            >
                {navigationSections.map((section) => {
                    const SectionIcon = section.icon;
                    const isExpanded = expandedSections.includes(section.id);
                    const hasItems = section.items && section.items.length > 0;
                    
                    return (
                        <div key={section.id} className="nav-section" role="group" aria-labelledby={`section-${section.id}`}>
                            {/* Section Header */}
                            <div 
                                id={`section-${section.id}`}
                                className={`nav-section-header ${!isCollapsed && hasItems ? 'clickable' : ''}`}
                                onClick={() => hasItems && toggleSection(section.id)}
                                onKeyDown={(e) => hasItems && handleKeyDown(e, toggleSection, section.id)}
                                data-tooltip={isCollapsed ? section.label : ''}
                                role={hasItems && !isCollapsed ? 'button' : 'heading'}
                                aria-level="2"
                                aria-expanded={hasItems && !isCollapsed ? isExpanded : undefined}
                                aria-controls={hasItems ? `section-items-${section.id}` : undefined}
                                tabIndex={hasItems && !isCollapsed ? 0 : -1}
                                aria-label={`${section.label} section${hasItems && !isCollapsed ? (isExpanded ? ', expanded' : ', collapsed') : ''}`}
                            >
                                <div className="nav-section-content">
                                    {/* <SectionIcon className="icon-spacing" aria-hidden="true" /> */}
                                    {!isCollapsed && (
                                        <>
                                            <span className="section-label">{section.label}</span>
                                            {hasItems && (
                                                <div className="expand-indicator" aria-hidden="true">
                                                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            {/* Section Items */}
                            {hasItems && (
                                <div 
                                    id={`section-items-${section.id}`}
                                    className={`nav-section-items ${isCollapsed ? 'collapsed-items' : ''} ${!isCollapsed && !isExpanded ? 'hidden' : ''}`}
                                    role="menu"
                                    aria-labelledby={`section-${section.id}`}
                                >
                                    {section.items.map((item, index) => {
                                        const ItemIcon = item.icon;
                                        return (
                                            <Nav.Item key={item.path} role="none">
                                                <Nav.Link 
                                                    as={Link} 
                                                    to={item.path}
                                                    className={`nav-item-link ${isActive(item.path) ? 'active' : ''}`}
                                                    data-tooltip={isCollapsed ? item.label : ''}
                                                    onClick={handleMobileNavClick}
                                                    role="menuitem"
                                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                                    aria-label={`Navigate to ${item.label}`}
                                                    tabIndex={0}
                                                >
                                                    <ItemIcon className="icon-spacing" aria-hidden="true" />
                                                    {<span>{item.label}</span>}
                                                </Nav.Link>
                                            </Nav.Item>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
                
                {/* Logout Section */}
                <div className="nav-section logout-section" role="group" aria-label="User actions">
                    <Nav.Item role="none">
                        <Nav.Link 
                            onClick={handleLogout} 
                            className="nav-item-link logout-link"
                            data-tooltip={isCollapsed ? 'Logout' : ''}
                            role="button"
                            aria-label="Logout from application"
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyDown(e, handleLogout)}
                        >
                            <FaSignOutAlt className="icon-spacing" aria-hidden="true" />
                            {!isCollapsed && <span>Logout</span>}
                        </Nav.Link>
                    </Nav.Item>
                </div>
            </Nav>
        </aside>
    );
};

export default Sidebar;
